module Data.Duration
  ( Duration
  , fromMs
  , fromStr
  , scale
  , sub
  ) where

-- this module is intended to be imported as D

import Prelude

import Control.Monad.Error.Class (throwError)
import Data.Argonaut (class DecodeJson, class EncodeJson, JsonDecodeError(..), decodeJson, encodeJson)
import Data.BigInt (BigInt)
import Data.BigInt as BigInt
import Data.Either (note)
import Data.Maybe (fromJust, fromMaybe)
import Data.Number (floor)
import Data.Time.Duration as DTD
import Test.QuickCheck (class Arbitrary, arbitrary)

{-
Represents a duration of time. 
-}
-- Implemented as a number of nanoseconds.
newtype Duration = Duration BigInt

fromMs :: Number -> Duration
fromMs ms = DTD.toDuration (DTD.Milliseconds ms)

instance DTD.Duration Duration where
  fromDuration :: Duration -> DTD.Milliseconds
  fromDuration (Duration nano) =
    let
      numInNano :: Number
      numInNano = BigInt.toNumber nano

      numInMs = numInNano / 1_000_000.0
    in
      DTD.Milliseconds numInMs

  toDuration :: DTD.Milliseconds -> Duration
  toDuration (DTD.Milliseconds ms) =
    let
      wholePart :: Number
      wholePart = floor ms

      fractionalPart :: Number
      fractionalPart = ms - wholePart

      wholePartNanos :: BigInt
      wholePartNanos = (fromMaybe zero (BigInt.fromNumber wholePart)) * (BigInt.fromInt 1_000_000)

      fractionalPartNanos :: BigInt
      fractionalPartNanos = fromMaybe zero (BigInt.fromNumber (fractionalPart * 1_000_000.0))
    in
      Duration (wholePartNanos + fractionalPartNanos)

instance Semigroup Duration where
  append (Duration n1) (Duration n2) = Duration (n1 + n2)

instance Monoid Duration where
  mempty = Duration zero

-- Scales a Duration by a scalar number
scale :: Int -> Duration -> Duration
scale factor (Duration d) = Duration (d * (BigInt.fromInt factor))

instance Show Duration where
  show (Duration d) = "(D.fromStr \"" <> BigInt.toString d <> "\")"

-- This function is the reverse operation of show, it recreates a value from its show representation
fromStr :: Partial => String -> Duration
fromStr str = Duration (fromJust (BigInt.fromString str))

sub :: Duration -> Duration -> Duration
sub (Duration d1) (Duration d2) = Duration (d1 - d2)

derive newtype instance Eq Duration
derive newtype instance Ord Duration

nanosPerSecond ∷ BigInt
nanosPerSecond = BigInt.fromInt 1_000_000_000

instance DecodeJson Duration where
  decodeJson json = do
    -- We decode to numbers because purescript ints are only 32 bits
    ({ seconds, nanos } :: { seconds :: Number, nanos :: Int }) <- decodeJson json

    let
      nanosBi :: BigInt
      nanosBi = BigInt.fromInt nanos
    -- fail on negative or bigger than a single second nanosecond amounts
    when (nanosBi < zero || nanosBi >= nanosPerSecond) do
      throwError (AtKey "nanos" (UnexpectedValue (encodeJson nanos)))

    secondsBi :: BigInt <-
      note
        (AtKey "seconds" (UnexpectedValue (encodeJson seconds)))
        (BigInt.fromNumber seconds)

    pure $ Duration (secondsBi * nanosPerSecond + nanosBi)

instance EncodeJson Duration where
  encodeJson (Duration d) = do
    let nanos = d `mod` nanosPerSecond
    let seconds = d `div` nanosPerSecond
    encodeJson
      { seconds: BigInt.toNumber seconds
      , nanos: BigInt.toNumber nanos
      }

instance Arbitrary Duration where
  arbitrary = do
    n1 :: Int <- arbitrary
    n2 :: Int <- arbitrary
    n3 :: Int <- arbitrary
    pure $ Duration (BigInt.fromInt n1 * BigInt.fromInt n2 * BigInt.fromInt n3)
